/**
 * Image nodes for the editor. Two variants so placement is switchable
 * (Images settings page / Insert menu): `imageBlock` sits on its own line,
 * `imageInline` flows within a paragraph. Both are atoms that store only an
 * `assetId` (+ alt, display width, an optional non-destructive crop rect, and
 * the source's natural pixel size) — never the bytes. A NodeView resolves the
 * assetId to a `data:` URL from the encrypted asset store (assets.ts) on demand.
 *
 * The NodeView also provides interactive **resize** (corner handles that drag
 * the display `width`) and renders a **crop** (`crop` = {x,y,w,h} fractions of
 * the natural image) non-destructively by clipping the full image inside a
 * viewport. Cropping is edited via a modal (ImageCropDialog) and baked into the
 * bytes only at DOCX export time; the stored asset always stays the original.
 *
 * They carry no `paraId`; a top-level `imageBlock` therefore projects to one
 * empty plain-text paragraph / block index in the flat model (like a table),
 * and positionMap refuses to map positions "inside" the atom, so highlights
 * can't attach to an image. Inline images contribute zero text characters, so
 * paragraph char-offsets (positionMap) are unaffected.
 */

import { Node, mergeAttributes, type NodeViewRenderer } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";

/** Crop rectangle as fractions (0..1) of the natural image. */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MIN_WIDTH = 40;
const MAX_WIDTH = 2000;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Lazily pull in the asset store only when a NodeView actually renders (browser
 * only). Keeping it out of this module's top-level imports means getEditorSchema
 * — used headlessly by tests and convert.ts — doesn't drag in fs/stores/settings
 * (which rely on webpack-only `require.context`). The dynamic import is cached.
 */
let assetsModulePromise: Promise<typeof import("./assets")> | null = null;
function loadAssetDataUrl(assetId: string): Promise<string | null> {
  assetsModulePromise = assetsModulePromise ?? import("./assets");
  return assetsModulePromise.then((m) => m.loadAssetDataUrl(assetId));
}

const imageAttributes = {
  assetId: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-asset-id"),
    renderHTML: (attrs: Record<string, any>) =>
      attrs.assetId ? { "data-asset-id": attrs.assetId } : {},
  },
  alt: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("alt"),
    renderHTML: (attrs: Record<string, any>) => (attrs.alt ? { alt: attrs.alt } : {}),
  },
  width: {
    default: null as number | null,
    parseHTML: (el: HTMLElement) => {
      const w = el.getAttribute("width");
      return w ? parseInt(w, 10) : null;
    },
    renderHTML: (attrs: Record<string, any>) => (attrs.width ? { width: attrs.width } : {}),
  },
  crop: {
    default: null as CropRect | null,
    parseHTML: (el: HTMLElement) => {
      const c = el.getAttribute("data-crop");
      if (!c) return null;
      try {
        return JSON.parse(c);
      } catch {
        return null;
      }
    },
    renderHTML: (attrs: Record<string, any>) =>
      attrs.crop ? { "data-crop": JSON.stringify(attrs.crop) } : {},
  },
  naturalWidth: {
    default: null as number | null,
    parseHTML: (el: HTMLElement) => {
      const v = el.getAttribute("data-nw");
      return v ? parseInt(v, 10) : null;
    },
    renderHTML: (attrs: Record<string, any>) =>
      attrs.naturalWidth ? { "data-nw": attrs.naturalWidth } : {},
  },
  naturalHeight: {
    default: null as number | null,
    parseHTML: (el: HTMLElement) => {
      const v = el.getAttribute("data-nh");
      return v ? parseInt(v, 10) : null;
    },
    renderHTML: (attrs: Record<string, any>) =>
      attrs.naturalHeight ? { "data-nh": attrs.naturalHeight } : {},
  },
};

const CORNERS = ["nw", "ne", "sw", "se"] as const;
type Corner = (typeof CORNERS)[number];

/** Shared vanilla NodeView: async <img>, crop-aware layout, corner resize handles. */
function imageNodeView(): NodeViewRenderer {
  return ({ editor, node, getPos }) => {
    const isBlock = node.type.name === "imageBlock";
    const wrapper = document.createElement(isBlock ? "figure" : "span");
    wrapper.className = isBlock ? "qda-image-block" : "qda-image-inline";
    wrapper.style.position = "relative";
    wrapper.style.display = isBlock ? "block" : "inline-block";

    const img = document.createElement("img");
    img.style.display = "block";
    img.style.maxWidth = "none";
    img.draggable = false;
    wrapper.appendChild(img);

    // Resize handles (hidden until the node is selected — see CSS).
    const handles: Record<Corner, HTMLElement> = {} as any;
    for (const corner of CORNERS) {
      const h = document.createElement("span");
      h.className = `qda-resize-handle qda-resize-${corner}`;
      h.addEventListener("pointerdown", (e) => startResize(e, corner));
      wrapper.appendChild(h);
      handles[corner] = h;
    }

    let currentNode: PMNode = node;
    let loadedAssetId: string | null = null;

    const naturalDims = (n: PMNode) => ({
      w: (n.attrs.naturalWidth as number) || img.naturalWidth || 0,
      h: (n.attrs.naturalHeight as number) || img.naturalHeight || 0,
    });

    const layout = (n: PMNode, widthOverride?: number) => {
      const nat = naturalDims(n);
      const dispW = clamp(widthOverride ?? (n.attrs.width as number) ?? nat.w ?? 320, MIN_WIDTH, MAX_WIDTH);
      const crop = n.attrs.crop as CropRect | null;
      wrapper.style.width = `${dispW}px`;
      if (crop && crop.w > 0 && crop.h > 0 && nat.w && nat.h) {
        const scale = dispW / (crop.w * nat.w);
        const fullW = nat.w * scale;
        const fullH = nat.h * scale;
        wrapper.style.height = `${crop.h * nat.h * scale}px`;
        wrapper.style.overflow = "hidden";
        img.style.position = "absolute";
        img.style.width = `${fullW}px`;
        img.style.height = "auto";
        img.style.left = `${-crop.x * fullW}px`;
        img.style.top = `${-crop.y * fullH}px`;
      } else {
        wrapper.style.height = "";
        wrapper.style.overflow = "";
        img.style.position = "static";
        img.style.width = `${dispW}px`;
        img.style.height = "auto";
        img.style.left = "";
        img.style.top = "";
      }
    };

    const render = (n: PMNode) => {
      currentNode = n;
      img.alt = n.attrs.alt || "";
      if (n.attrs.assetId !== loadedAssetId) {
        loadedAssetId = n.attrs.assetId;
        const id = loadedAssetId;
        img.removeAttribute("src");
        if (id) {
          loadAssetDataUrl(id).then((url) => {
            if (loadedAssetId !== id) return; // superseded by a later update
            if (url) img.src = url;
            else img.alt = `[missing image${n.attrs.alt ? `: ${n.attrs.alt}` : ""}]`;
          });
        }
      }
      layout(n);
    };

    // Re-apply layout once natural size is known (for nodes lacking the attrs).
    img.addEventListener("load", () => layout(currentNode));

    function startResize(e: PointerEvent, corner: Corner) {
      if (!editor.isEditable || typeof getPos !== "function") return;
      e.preventDefault();
      e.stopPropagation();
      const nat = naturalDims(currentNode);
      const startX = e.clientX;
      const startW = clamp((currentNode.attrs.width as number) ?? nat.w ?? 320, MIN_WIDTH, MAX_WIDTH);
      const leftSide = corner === "nw" || corner === "sw";
      let live = startW;
      const move = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        live = clamp(leftSide ? startW - dx : startW + dx, MIN_WIDTH, MAX_WIDTH);
        layout(currentNode, live);
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        commitWidth(Math.round(live));
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    }

    function commitWidth(w: number) {
      const pos = typeof getPos === "function" ? getPos() : null;
      if (pos == null) return;
      const view = editor.view;
      const target = view.state.doc.nodeAt(pos);
      if (!target) return;
      view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...target.attrs, width: w }));
    }

    render(node);

    return {
      dom: wrapper,
      update: (updated: PMNode) => {
        if (updated.type.name !== node.type.name) return false;
        render(updated);
        return true;
      },
      selectNode: () => {
        if (editor.isEditable) wrapper.classList.add("qda-image-selected");
      },
      deselectNode: () => {
        wrapper.classList.remove("qda-image-selected");
      },
      // Atom NodeView manages its own DOM; don't let ProseMirror re-read it.
      ignoreMutation: () => true,
      // Keep handle drags from starting a ProseMirror drag/selection.
      stopEvent: (e: Event) =>
        !!(e.target as HTMLElement | null)?.classList?.contains("qda-resize-handle"),
    };
  };
}

export const ImageBlock = Node.create({
  name: "imageBlock",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes: () => imageAttributes,
  parseHTML: () => [{ tag: 'img[data-image="block"]' }],
  renderHTML: ({ HTMLAttributes }) => ["img", mergeAttributes(HTMLAttributes, { "data-image": "block" })],
  addNodeView: imageNodeView,
});

export const ImageInline = Node.create({
  name: "imageInline",
  group: "inline",
  inline: true,
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes: () => imageAttributes,
  parseHTML: () => [{ tag: 'img[data-image="inline"]' }, { tag: "img[data-asset-id]" }],
  renderHTML: ({ HTMLAttributes }) => ["img", mergeAttributes(HTMLAttributes, { "data-image": "inline" })],
  addNodeView: imageNodeView,
});
